import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import useRoleCheck from "../auth/useRoleCheck";
import { MODULES, ROLE_GROUPS } from "../auth/roles";
import RoleGuard from "../auth/RoleGuard";
import AccessDenied from "./ui/AccessDenied";

/*React-Bootstrap components */
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Nav from "react-bootstrap/Nav";
import Button from "react-bootstrap/Button";
import {
  useDeleteProject,
  useGetProjectsPagination,
} from "../services/Projects/projectsAPI";
import { useExportCsv } from "../services/Shared/sharedAPI";
import DataGrid from "./dataGrid/DataGrid";
import DeleteModal from "./ui/DeleteModal";
import formatDateHelper from "./utils/dateFormat";

const tabOptions = [
  {
    key: "ostali",
    label: "Projekti (v izvajanju ali zaključeni)",
    fullLabel: "Projekti (v izvajanju ali zaključeni)",
    path: "/Projekti",
  },
  {
    key: "ORUD",
    label: "Skrbniški pregled",
    fullLabel: "Skrbniški pregled",
    path: "/Projekti/ORUD",
  },
  {
    key: "clanica",
    label: "Projekti članice",
    fullLabel: "Projekti članice",
    path: "/Projekti/Clanica",
  },
];

const PROJECTS_V2_GRID_STATE_KEY = "projectsV2GridState";

const ProjectsV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isGlobalni, isEditor, isFakultetniSkrbnik, isAll, clanicaID } =
    useRoleCheck(MODULES.PROJEKTI);

  // Define function to determine if the delete action should be shown for a given row based on user roles and clanica association
  const canDeleteProject = (row) => {
    const rowClanicaId = row?.original?.clanicaSkrbnikID;
    const isSameClanica = Number(rowClanicaId) === Number(clanicaID);
    return isEditor || (isFakultetniSkrbnik && isSameClanica);
  };

  // Determine active tab based on current route
  const activeTab = React.useMemo(() => {
    const path = location.pathname;
    if (path.includes("/Clanica")) return "clanica";
    if (path.includes("/ORUD")) return "ORUD";
    return "ostali";
  }, [location.pathname]);

  const visibleTabs = React.useMemo(() => {
    return tabOptions.filter((tab) => {
      if (tab.key === "ostali") return isAll;
      if (tab.key === "ORUD") return isGlobalni;
      if (tab.key === "clanica") return isFakultetniSkrbnik;
      return false;
    });
  }, [isGlobalni, isFakultetniSkrbnik, isAll]);

  // Determine API endpoint based on active tab
  const apiEndpoint = React.useMemo(() => {
    switch (activeTab) {
      case "ORUD":
        return "Projekti/seznamProjektovKombiniraniV2";
      case "clanica":
        return "Projekti/seznamProjektovKombiniraniV2";
      case "ostali":
        return "Projekti/seznamOstalihProjektovFakultetniV2";
    }
  }, [activeTab]);

  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const [sorting, setSorting] = useState([]);
  const [globalSearch, setGlobalSearch] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [isGridStateHydrated, setIsGridStateHydrated] = useState(false);

  const storageKey = React.useMemo(
    () => `${PROJECTS_V2_GRID_STATE_KEY}:${activeTab}`,
    [activeTab],
  );

  useEffect(() => {
    setIsGridStateHydrated(false);

    const rawState = sessionStorage.getItem(storageKey);
    if (!rawState) {
      setSorting([]);
      setGlobalSearch("");
      setColumnFilters([]);
      setIsGridStateHydrated(true);
      return;
    }

    try {
      const parsedState = JSON.parse(rawState);
      setSorting(
        Array.isArray(parsedState?.sorting) ? parsedState.sorting : [],
      );
      setGlobalSearch(
        typeof parsedState?.globalSearch === "string"
          ? parsedState.globalSearch
          : "",
      );
      setColumnFilters(
        Array.isArray(parsedState?.columnFilters)
          ? parsedState.columnFilters
          : [],
      );
    } catch {
      setSorting([]);
      setGlobalSearch("");
      setColumnFilters([]);
    }

    setIsGridStateHydrated(true);
  }, [storageKey]);

  useEffect(() => {
    if (!isGridStateHydrated) return;

    sessionStorage.setItem(
      storageKey,
      JSON.stringify({
        sorting,
        globalSearch,
        columnFilters,
      }),
    );
  }, [storageKey, sorting, globalSearch, columnFilters, isGridStateHydrated]);

  useEffect(() => {
    // Reset page index to 0 when search or sorting changes
    if (!isGridStateHydrated) return;

    setPagination((old) => ({
      ...old,
      pageIndex: 0,
    }));
  }, [globalSearch, sorting, columnFilters, isGridStateHydrated]);

  // Fetch paginated projects based on current pagination, sorting, search and filters
  const { data: projects, isLoading } = useGetProjectsPagination(
    pagination,
    sorting,
    globalSearch,
    columnFilters,
    apiEndpoint,
  );

  // Set up export file mutation for exporting current grid state to CSV
  const exportFileMutation = useExportCsv(
    pagination,
    sorting,
    globalSearch,
    columnFilters,
    apiEndpoint,
  );

  //DELETE project
  const {
    mutate: deleteProject,
    isPending: isDeletePending,
    isError: isDeleteError,
    error: deleteError,
    reset: resetDeleteError,
  } = useDeleteProject();

  //Fetch filters (širants) for the data grid (dropdowns)
  const filters = projects?.filters;

  //Set up columns for the data grid, including filters(šifrants) options for dropdown filters
  const columns = React.useMemo(() => {
    const projectColumns = [
      {
        header: "Vodilna članica",
        accessorKey: "clanicaSkrbnik",
        enableColumnFilter: true,
        meta: {
          filterVariant: "checkboxFilter",
          filterOptions: filters?.clanice || [],
        },
      },
      {
        header: "Program",
        accessorKey: "programParent",
        enableColumnFilter: true,
        meta: {
          filterVariant: "checkboxFilter",
          filterOptions: filters?.programi || [],
        },
      },
      {
        header: "Podprogram",
        accessorKey: "program",
      },
      {
        header: "Naslov projekta",
        accessorKey: "naziv",
      },
      {
        header: "Vloga UM v projektu",
        accessorKey: "uMvloga",
        enableColumnFilter: true,
        meta: {
          filterVariant: "checkboxFilter",
          filterOptions: filters?.vloge || [],
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: true,
        meta: {
          filterVariant: "checkboxFilter",
          filterOptions: filters?.statusi || [],
        },
      },
      {
        header: "Vodja projekta",
        accessorKey: "vodjaProjekta",
        cell: (info) => {
          return (
            info.row.original?.vodjaProjekta ||
            info.row.original?.vodjaProjektaNaUM ||
            info.row.original?.vodja ||
            ""
          );
        },
      },
      {
        header: "Začetek projekta",
        accessorKey: "zacetek",
        enableColumnFilter: true,
        meta: {
          filterVariant: "dateRangeFilter",
        },
        cell: (info) => {
          const value = info.getValue();
          if (!value) return "";

          return formatDateHelper(value);
        },
      },
      {
        header: "Konec projekta",
        accessorKey: "konec",
        enableColumnFilter: true,
        meta: {
          filterVariant: "dateRangeFilter",
        },
        cell: (info) => {
          const value = info.getValue();
          if (!value) return "";

          return formatDateHelper(value);
        },
      },
    ];

    return projectColumns;
  }, [filters?.statusi, filters?.clanice, filters?.programi, filters?.vloge]);

  //handle navigate by project ID
  const handlePodrobnostiClick = (row) => {
    navigate(`/Projekti/Podrobnosti/${row.original.id}`);
  };

  const [projectToDelete, setProjectToDelete] = useState(null);

  const handleDeleteClick = (row) => {
    resetDeleteError();
    setProjectToDelete(row.original);
  };

  const handleConfirmDelete = () => {
    deleteProject(projectToDelete.id, {
      onSuccess: () => {
        resetDeleteError();
        setProjectToDelete(null);
      },
      onError: () => {
        setTimeout(() => {
          resetDeleteError();
        }, 5000);
      },
    });
  };

  return (
    <RoleGuard
      moduleKey={MODULES.PROJEKTI}
      roles={ROLE_GROUPS.ALL}
      fallback={<AccessDenied />}
    >
      <Container fluid>
        <>
          <Row>
            <Col className="main-header font-weight-bold">Projekti</Col>
          </Row>
          <Row>
            <Col className="header-divider">
              <hr />
            </Col>
          </Row>

          <Row className="mb-3 align-items-center">
            <Col>
              <div className="deminimis-tabs-wrap">
                <Nav
                  variant="tabs"
                  activeKey={activeTab}
                  onSelect={(selectedKey) => {
                    const selectedTab = tabOptions.find(
                      (tab) => tab.key === selectedKey,
                    );
                    if (selectedTab) {
                      navigate(selectedTab.path);
                    }
                  }}
                  className="deminimis-tabs"
                >
                  {visibleTabs.map((tab) => (
                    <Nav.Item key={tab.key}>
                      <Nav.Link eventKey={tab.key} title={tab.fullLabel}>
                        {tab.label}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
              </div>
            </Col>
          </Row>

          <Row className="mb-2">
            <Col>
              <div className="float-end">
                <RoleGuard
                  moduleKey={MODULES.PROJEKTI}
                  roles={ROLE_GROUPS.EDITORS_AND_SKRBNIK}
                >
                  <Link
                    className="programs-button-add-text"
                    to="/Projekti/DodajProjekt/"
                  >
                    <Button variant="outline-primary" size="sm">
                      Dodaj projekt
                    </Button>
                  </Link>
                </RoleGuard>
              </div>
            </Col>
          </Row>

          <Row>
            <Col>
              <DataGrid
                data={projects?.data || []}
                columns={columns}
                onPaginationChange={setPagination}
                onSortingChange={setSorting}
                sorting={sorting}
                pagination={pagination}
                columnFilters={columnFilters}
                onColumnFiltersChange={setColumnFilters}
                pageCount={projects?.totalPages || 0}
                loading={isLoading}
                hasGlobalSearch={true}
                searchGlobalPlaceholder="Iskanje po nazivu, programu, podprogramu ali akronimu..."
                globalSearch={globalSearch}
                onGlobalSearchChange={setGlobalSearch}
                hasActionColumn={true}
                onPodrobnostiActionClick={handlePodrobnostiClick}
                onDeleteActionClick={handleDeleteClick}
                canDelete={canDeleteProject}
                hasExportCsvButton={true}
                exportFileMutation={exportFileMutation}
              />
            </Col>
          </Row>
        </>
      </Container>

      <DeleteModal
        show={!!projectToDelete}
        onHide={() => {
          setProjectToDelete(null);
          resetDeleteError();
        }}
        onConfirm={handleConfirmDelete}
        text="Ali ste prepričani, da želite izbrisati projekt:"
        naziv={projectToDelete?.naziv}
        isError={isDeleteError}
        errorMessage={"Napaka pri brisanju. Prosimo, poskusite znova."}
        isPending={isDeletePending}
      />
    </RoleGuard>
  );
};

export default ProjectsV2;
